import { useGetChatroomStats } from '@pba/react-network';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { BellCircleIcon, Col, InboxLineCircleIcon, ListCircleIcon, MessagesCircleIcon, Row, Text } from 'ui';
import { useNotificationBadgesQuery } from 'features/users';
import { useTodosWidget } from '../context';

type P = {
  hideTodosFeedCta?: boolean;
};

const TodosWidgetEmptyState = ({ hideTodosFeedCta }: P) => {
  const { t } = useTranslation();
  const { activeKey } = useTodosWidget();
  const { data } = useGetChatroomStats();

  const unreadMessages = data?.reduce((total, item) => total + item.unread, 0) ?? 0;

  const { data: badgesData } = useNotificationBadgesQuery();
  const badges = badgesData?.total ?? 0;

  const emptyStateAttributes = useMemo(() => {
    if (activeKey === 'tomorrow') {
      return {
        description: t('Any to-dos assigned to you for tomorrow will appear here'),
        icon: <InboxLineCircleIcon />,
        title: t('No To-Dos for tomorrow'),
      };
    }

    if (unreadMessages > 0) {
      return {
        description: (
          <Trans
            components={{ strong: <strong /> }}
            i18nKey="There are unread messages waiting for your attention. See what’s happening there"
            t={t}
            values={{
              unreadMessages,
            }}
          />
        ),
        icon: <MessagesCircleIcon />,
        link: '/messages',
        linkLabel: t('Check my messages'),
        title: t('Well done on finishing your To-Dos for today'),
      };
    }

    if (badges > 0) {
      return {
        description: (
          <Trans
            components={{ strong: <strong /> }}
            i18nKey="There are new notifications waiting for your attention. See what’s happening there"
            t={t}
            values={{
              newNotifications: badges,
            }}
          />
        ),
        icon: <BellCircleIcon />,
        link: '/notifications',
        linkLabel: t('Open notifications'),
        title: t('Great job on completing your To-Dos for today'),
      };
    }

    return {
      description: t(
        `Stay organized and accomplish tasks throughout the day. Explore more features as you complete your To-Dos or wait for new ones`,
      ),
      hidden: hideTodosFeedCta,
      icon: <ListCircleIcon />,
      link: '/feed',
      linkLabel: t('See what’s happening'),
      title: t('Your To-Do adventure starts here'),
    };
  }, [activeKey, unreadMessages, badges, t, hideTodosFeedCta]);

  return (
    <div className="todos-widget-empty-state">
      <Row align="middle" className="todos-widget-empty-state__content" gutter={[0, 'ms']} justify="center">
        <Col span={24}>{emptyStateAttributes.icon}</Col>
        <Col span={24}>
          <Text strong>{emptyStateAttributes.title}</Text>
        </Col>
        <Col span={24}>
          <Text>{emptyStateAttributes.description}</Text>
        </Col>
        {Boolean(emptyStateAttributes.hidden) || !emptyStateAttributes.link ? null : (
          <Col span={24}>
            <NavLink to={emptyStateAttributes.link}>{emptyStateAttributes.linkLabel}</NavLink>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default TodosWidgetEmptyState;
